import { useCallback, useState, type ReactNode } from 'react';
import ReactOtpInput, { type InputProps } from 'react-otp-input';
import get from 'lodash/get';
import { CFormLabel, CFormFeedback } from '@coreui/react';
import s from './OtpInput.module.scss';
import type { IFieldComponentProps } from './types';

interface IOtpInputProps extends IFieldComponentProps<string> {
  label?: ReactNode;
  numInputs?: number;
  separator?: ReactNode;
}

/** No input is focused. */
const NO_INPUT = -1;

const OtpInput = ({ field, form, label, numInputs = 6, separator = '-' }: IOtpInputProps) => {
  const { name, value } = field;
  const { touched, errors, setFieldValue } = form;
  // The input that has the focus, for the focus class (react-otp-input 2 had a focusStyle prop for it); the first
  // one is focused on mount.
  const [focusedInput, setFocusedInput] = useState(0);

  const handleChange = useCallback(
    (v: string) => {
      setFieldValue(name, v);
    },
    [name, setFieldValue],
  );

  // Bug: react-otp-input 2 dropped the className and onBlur this component passed (form-control / is-invalid, and
  // marking the field touched on blur), so the inputs never show the invalid state and the field is touched on
  // submit only; kept that way.
  // The markup of react-otp-input 2: each input in its own flex box with the separator after it, maxLength 1, no
  // aria-label and inputmode (which 3 adds).
  const renderInput = useCallback(
    (
      {
        'aria-label': _ariaLabel,
        inputMode: _inputMode,
        onFocus,
        onBlur,
        ...inputProps
      }: InputProps,
      index: number,
    ) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <input
          {...inputProps}
          className={index === focusedInput ? `${s.input} ${s.focusInput}` : s.input}
          maxLength={1}
          onFocus={(event) => {
            setFocusedInput(index);
            onFocus(event);
          }}
          onBlur={(event) => {
            setFocusedInput(NO_INPUT);
            onBlur(event);
          }}
        />
        {index < numInputs - 1 ? <span>{separator}</span> : null}
      </div>
    ),
    [focusedInput, numInputs, separator],
  );

  return (
    <div>
      <div className="mb-3">
        <CFormLabel htmlFor={name}>{label}</CFormLabel>
        <ReactOtpInput
          value={value}
          numInputs={numInputs}
          onChange={handleChange}
          renderInput={renderInput}
          containerStyle={s.container}
          inputType="tel"
          shouldAutoFocus
        />
        {/* d-block: Bootstrap shows .invalid-feedback only as a sibling of an .is-invalid input, which the OTP
            inputs are not. */}
        {get(touched, name) && get(errors, name) && (
          <CFormFeedback invalid className="help-block d-block">
            {errors[name]}
          </CFormFeedback>
        )}
      </div>
    </div>
  );
};

export default OtpInput;
